import React from 'react';
import { cn } from './cn';

/**
 * Button — the prototype's brand button.
 *
 * Shape: pill (border-radius: 999px) — matches the `.btn` selector in
 * cms-redesign-mockup.html. Filled variants (coral, primary) carry a small
 * colored drop shadow + an inset white highlight on the top edge so they
 * feel solid, like a tab key. Hover lifts the button 1px (translateY) to
 * give the brand its trademark "alive" feel.
 *
 * Variants:
 *   - default   surface (white) with sand border, navy text — toolbar action
 *   - coral     filled coral CTA ("+ New session" / "Sign in with…")
 *   - primary   filled teal-green CTA (Save)
 *   - ghost     transparent, no border, muted text — quiet "Cancel"-style
 *   - danger    cream surface with coral border, coral text — destructive
 *
 * Sizes: sm (30px, prototype `.btn.sm`) | md (36px, prototype `.btn`).
 */
const VARIANT = {
  default: 'bg-surface text-navy border-sand hover:bg-cream hover:border-line-strong shadow-sm',
  coral:   'bg-coral text-white border-coral hover:bg-coral-dark hover:border-coral-dark',
  primary: 'bg-teal-green text-white border-teal-green hover:bg-teal-green-dark hover:border-teal-green-dark',
  ghost:   'bg-transparent text-text-light border-transparent hover:bg-cream hover:text-navy',
  danger:  'bg-cream text-coral-dark border-coral/50 hover:bg-coral-soft hover:border-coral',
};

// Brand drop-shadow + inset top highlight on the two filled variants — gives
// them the "raised key" feel from the prototype.
const VARIANT_BOX_SHADOW = {
  coral:   '0 2px 6px #e58a6a40, inset 0 1px 0 #FFFFFF26',
  primary: '0 2px 6px #75a29340, inset 0 1px 0 #FFFFFF24',
};

const SIZE = {
  sm: 'h-[30px] px-3 text-[12px]',
  md: 'h-9 px-4 text-[13px]',
};

export function Button({
  variant = 'default',
  size   = 'md',
  leadingIcon,
  trailingIcon,
  children,
  className,
  style,
  type = 'button',
  ...rest
}) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-pill border',
        'font-sans font-semibold leading-none whitespace-nowrap',
        'transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none',
        'hover:-translate-y-px',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      style={VARIANT_BOX_SHADOW[variant] ? { boxShadow: VARIANT_BOX_SHADOW[variant], ...style } : style}
      {...rest}
    >
      {leadingIcon && <span className="shrink-0 inline-flex">{leadingIcon}</span>}
      {children}
      {trailingIcon && <span className="shrink-0 inline-flex">{trailingIcon}</span>}
    </button>
  );
}

export default Button;
